import { DateObject, type NetworkType, WithSkeleton } from '@capsicum/common';
import { CardCarousel, Skeleton, SkeletonItem } from '@capsicum/common-web';
import { type MyCollectible } from '@capsicum/features/digital-collectibles/shared';
import { CollectibleCard } from '@capsicum/features/digital-collectibles/web';
import { styled } from 'styled-components';

import { WalletNoCollectiblesCard } from '../../../../ui';

type CollectiblesComponentProps = {
	collectibles: Array<MyCollectible>;
	onCollectibleClick: (collectibleId?: string) => void;
};

export const WalletCollectiblesCarouselSkeleton = () => (
	<S.Container>
		<S.CardCarouselStyled
			arrowsVisible={false}
			cardWidth={143}
			disabled
			showHeader={false}
			testID="wallet-collectible-carousel-skeleton"
		>
			<SkeletonItem height={227} width={173}></SkeletonItem>
			<SkeletonItem height={227} width={173}></SkeletonItem>
			<SkeletonItem height={227} width={173}></SkeletonItem>
			<SkeletonItem height={227} width={173}></SkeletonItem>
			<SkeletonItem height={227} width={173}></SkeletonItem>
			<SkeletonItem height={227} width={173}></SkeletonItem>
		</S.CardCarouselStyled>
	</S.Container>
);

const CarouselCard = ({
	collectible,
	onCollectibleClick
}: {
	collectible: MyCollectible;
	onCollectibleClick: (collectibleId?: string) => void;
}) => (
	<CollectibleCard
		availableAt={collectible.unlockingDate ? new DateObject(collectible.unlockingDate) : undefined}
		backgroundImageUrl={collectible.partner?.rewardsBackground || ''}
		key={`${collectible.summary?.collectibleId} ${collectible.partner?.id}` || ''}
		network={collectible.blockchain?.networkType as NetworkType}
		nftImageUrl={collectible.summary?.imageUrl || ''}
		optionType={collectible.optionType || ''}
		status={collectible.summary?.status}
		subtitle={collectible.summary?.description || ''}
		title={collectible.summary?.name || ''}
		onActivate={() => onCollectibleClick(collectible.summary?.collectibleId)}
	/>
);

const CollectiblesComponent = ({ collectibles, onCollectibleClick }: CollectiblesComponentProps) => {
	return collectibles.length === 0 ? (
		<WalletNoCollectiblesCard />
	) : (
		<S.Container>
			<S.CardCarouselStyled
				arrowsVisible={false}
				cardWidth={173}
				showHeader={false}
				testID="wallet-collectible-carousel"
			>
				{collectibles.map((item, index) => (
					<CarouselCard
						collectible={item}
						key={item.partner?.id && item.partner?.id + index}
						onCollectibleClick={onCollectibleClick}
					/>
				))}
			</S.CardCarouselStyled>
		</S.Container>
	);
};

export const WalletCollectiblesCarousel = WithSkeleton(CollectiblesComponent, WalletCollectiblesCarouselSkeleton);

namespace S {
	export const Container = styled.div`
		width: 100%;
	`;
	export const CardCarouselStyled = styled(CardCarousel)`
		margin: 0;
		padding: 0;
	`;
	export const SkeletonStyled = styled(Skeleton)`
		flex-direction: row;
	`;
}
